import React, { useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Circle, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * InvestorCompletenessPopover
 * ---------------------------
 * Clickable pill that opens a popup checklist showing exactly what the
 * investor still has to fill in to reach 100% profile completeness. Every
 * unchecked item links straight to the relevant page.
 */
export default function InvestorCompletenessPopover({ me, pct }) {
  const [open, setOpen] = useState(false);
  const investor = me?.investor || {};
  const prefs = me?.preferences || {};

  // Close on Escape when the modal is open.
  React.useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  // Every checklist row: {key, label, done, to (route to fix it)}
  const checks = [
    { key: "name",       label: "Fund / Firm name",     done: !!investor.investor_name,          to: "/investor/profile" },
    { key: "type",       label: "Investor type",        done: !!investor.investor_type,          to: "/investor/profile" },
    { key: "contact",    label: "Contact person",       done: !!investor.contact_person,         to: "/investor/profile" },
    { key: "mobile",     label: "Mobile number",        done: !!investor.mobile,                 to: "/investor/profile" },
    { key: "city",       label: "City",                 done: !!investor.city,                   to: "/investor/profile" },
    { key: "aum",        label: "AUM",                  done: !!investor.aum,                    to: "/investor/profile" },
    { key: "portfolio",  label: "Portfolio size",       done: !!investor.portfolio_size,         to: "/investor/profile" },
    { key: "years",      label: "Years active",         done: !!investor.years_active,           to: "/investor/profile" },
    { key: "sectors",    label: "Preferred sectors",    done: (prefs.preferred_sectors || []).length > 0, to: "/investor/preferences" },
    { key: "stages",     label: "Preferred stages",     done: (prefs.preferred_stages  || []).length > 0, to: "/investor/preferences" },
    { key: "ticket",     label: "Ticket size range",    done: !!prefs.ticket_size_min && !!prefs.ticket_size_max, to: "/investor/preferences" },
    { key: "risk",       label: "Risk appetite",        done: !!prefs.risk_appetite,             to: "/investor/preferences" },
    { key: "return",     label: "Expected return",      done: !!prefs.expected_return,           to: "/investor/preferences" },
    { key: "horizon",    label: "Investment horizon",   done: !!prefs.investment_horizon,        to: "/investor/preferences" },
    { key: "kyc",        label: "KYC submitted",        done: (investor.kyc_status || "Not Submitted") !== "Not Submitted", to: "/investor/kyc" },
  ];

  const remaining = checks.filter((c) => !c.done);
  const barTone = pct >= 80 ? "bg-emerald-600"
    : pct >= 50 ? "bg-amber-500"
    : "bg-rose-500";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full text-left border border-navy/10 bg-gradient-to-br from-emerald-50/60 via-white to-white p-4 rounded-lg hover:border-emerald-300 transition-colors cursor-pointer"
        data-testid="completeness-popover-trigger"
        title="See what to fill in next"
      >
        <div className="overline flex items-center gap-1.5 text-emerald-700">Profile Completeness</div>
        <div className="font-heading text-2xl text-ink mt-1">{pct}%</div>
        <div className="mt-2 h-1.5 w-full bg-navy/10 rounded-full overflow-hidden">
          <div className={`h-full ${barTone} transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[11px] text-ink-muted mt-1.5">
          {remaining.length === 0 ? "You're all set!" : `${remaining.length} item${remaining.length === 1 ? "" : "s"} left — click to see`}
        </div>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[85] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="completeness-modal"
        >
          <div
            className="max-w-md w-full bg-white rounded-xl shadow-2xl border border-navy/10 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-navy/10 flex items-center justify-between">
              <div>
                <div className="overline text-emerald-700">Profile Completeness · {pct}%</div>
                <div className="font-heading text-ink text-lg mt-1">
                  {remaining.length === 0 ? "You're all set!" : "Finish these to reach 100%"}
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink" data-testid="completeness-modal-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-1.5 overflow-y-auto">
              {checks.map((c) => (
                <Link
                  key={c.key}
                  to={c.to}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-2 px-2 py-2 rounded transition-colors ${
                    c.done ? "opacity-60" : "hover:bg-emerald-50 group"
                  }`}
                  data-testid={`completeness-item-${c.key}`}
                >
                  {c.done
                    ? <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
                    : <Circle size={16} className="text-ink-muted flex-shrink-0" />
                  }
                  <span className={`text-sm flex-1 ${c.done ? "text-ink-muted line-through" : "text-ink"}`}>{c.label}</span>
                  {!c.done && <ArrowRight size={12} className="text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" />}
                </Link>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
